import React, { Component } from "react";

import * as R from "ramda";

import { Box, Button, Flex, FlexLink, Image, Link, Text } from "src/components";
import { Arrow } from "src/icons";

import { defaultPrevented } from "src/utils";

import { getDay } from "date-fns";

import LocationAddress from "./LocationAddress";
import { getLocationType } from "./types";

const todayIndex = getDay(new Date());

const LocationHours = ({ hours }) => {
  if (!hours) {
    return null;
  }

  return (
    <Flex my={2} flexDirection="column">
      {hours.map(({ day, hours }, index) => {
        const today = index === todayIndex;
        return (
          <Flex
            flex={1}
            key={day}
            borderColor="divider"
            bg={today ? "green" : undefined}
            flexDirection="row"
            align="center"
            justify="center"
            p={1}
            px={2}
          >
            <Text
              fontSize={1}
              fontWeight="bold"
              color={today ? "white" : undefined}
              flex={1}
            >
              {day}
            </Text>
            <Text
              fontSize={1}
              fontWeight="light"
              color={today ? "white" : undefined}
              flex={1}
              align="flex-end"
            >
              {hours || "Closed"}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

class Location extends Component {
  handleMouseOver = e => {
    e.preventDefault();
    this.props.onHover && this.props.onHover();
  };

  handleMouseOut = e => {
    e.preventDefault();
    this.props.onBlur && this.props.onBlur();
  };

  handleClick = e => {
    e.preventDefault();
    this.props.onClick();
  };

  shouldComponentUpdate(nextProps) {
    return nextProps.hovered !== this.props.hovered;
  }

  render() {
    const { focused, anyHovered, hovered, location, t } = this.props;

    const locationType = getLocationType(location, t);

    return (
      <Flex
        onMouseOver={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
        p={2}
        style={{
          cursor: "pointer",
        }}
        flexDirection="column"
        bg={focused ? "green" : hovered ? "#f3f3f3" : undefined}
        onClick={this.handleClick}
        to="#"
      >
        <Flex flexDirection="row">
          <Box flex={1} mr={2}>
            <Link
              color={focused ? "white" : "green"}
              fontSize={4}
              fontWeight="semibold"
              textDecoration={hovered ? "underline" : "none"}
              onClick={this.handleClick}
              to="#"
            >
              {location.name}
            </Link>

            {locationType && (
              <Text mb={1} fontSize={2} color="muted">
                {locationType.label}
              </Text>
            )}

            <LocationAddress
              color={focused ? "white" : "grayText"}
              linkColor={focused ? "white" : "green"}
              {...location}
            />
          </Box>
          {locationType && (
            <Flex my={1} flexDirection="column" align="center">
              <img
                src={locationType.pinImage}
                alt="Locator pin icon"
                style={{
                  height: 36,
                }}
              />
            </Flex>
          )}
        </Flex>

        <Text mt={1} fontSize={2} color={focused ? "white" : "grayText"}>
          {location.hours &&
            (location.hoursToday
              ? `Open today from ${location.hoursToday}`
              : "Closed today")}
        </Text>
      </Flex>
    );
  }
}

const LocationDetails = ({ location, t }) => (
  <Box>
    <Location location={location} t={t} />
    <LocationHours {...location} />
  </Box>
);

const ArrowLink = ({ onClick, direction, ...props }) => {
  const arrow = (
    <Arrow
      direction={direction}
      size={14}
      color={onClick ? "green" : "muted"}
      hover={{ color: "green" }}
      {...props}
    />
  );

  return onClick ? (
    <Link to="#" onClick={defaultPrevented(onClick)}>
      {arrow}
    </Link>
  ) : (
    arrow
  );
};

const Pagination = ({
  page,
  pageCount,
  pageSize,
  setPage,
  totalLocationCount,
}) => (
  <Flex align="center" m={2}>
    <Text
      flex={1}
      fontSize={2}
      color="muted"
      justify="center"
      style={{ lineHeight: "1rem" }}
      mr={1}
    >
      Showing {page * pageSize + 1} to{" "}
      {Math.min((page + 1) * pageSize, totalLocationCount)}
    </Text>
    <Flex>
      <ArrowLink
        onClick={page !== 0 && (() => setPage(page - 1))}
        direction="left"
      />
      <Box ml={1}>
        <ArrowLink
          onClick={page < pageCount - 1 && (() => setPage(page + 1))}
          direction="right"
        />
      </Box>
    </Flex>
  </Flex>
);

const LocationList = ({
  loading,
  locations,
  focusLocation,
  focusedLocation,
  currentLocationId,
  setCurrentLocationId,
  t,
  ...props
}) => {
  if (loading) {
    return <Flex {...props} flexDirection="column" />;
  }

  return (
    <Flex {...props} flexDirection="column">
      {!focusedLocation && props.pageCount > 1 && <Pagination {...props} />}
      <Box flex={1} style={{ overflowY: "auto" }}>
        {focusedLocation ? (
          <Flex flexDirection="column" key={focusedLocation.id}>
            <Link
              onClick={defaultPrevented(focusLocation)}
              to="#"
              flex={1}
              p={2}
              borderWidth={0}
              color="white"
              bg="green"
              textDecoration="none"
              flexDirection="row"
              align="center"
            >
              <Arrow direction="left" size={14} />{" "}
              <Text ml={2}>Back to listing</Text>
            </Link>
            <LocationDetails location={focusedLocation} t={t} />
          </Flex>
        ) : (
          locations &&
          locations.map(location => (
            <Location
              key={location.id}
              location={location}
              hovered={currentLocationId === location.id}
              anyHovered={!!currentLocationId}
              onHover={() => setCurrentLocationId(location.id)}
              onBlur={() => setCurrentLocationId(undefined)}
              t={t}
              onClick={() => {
                focusLocation(location);
                setCurrentLocationId(location.id);
              }}
            />
          ))
        )}
      </Box>
    </Flex>
  );
};

export default LocationList;



// WEBPACK FOOTER //
// ./src/locator/LocationList.js